'use client';

// Thin React context that exposes a RealtimeSubscriber to client components.
// Apps construct the subscriber once (typically in a root layout) using their
// own anon-key Supabase client and pass it into the provider. Tests can pass
// in the in-memory transport's subscriber and exercise hooks without a
// network.

import {
    createContext,
    useContext,
    type ReactNode,
} from 'react';

import type { RealtimeSubscriber } from '../../types/realtime/subscriber';

const RealtimeContext = createContext<RealtimeSubscriber | null>(null);

interface RealtimeProviderProps {
    subscriber: RealtimeSubscriber;
    children: ReactNode;
}

/**
 * Mounts a RealtimeSubscriber into React context. Components downstream read
 * it via `useRealtimeSubscriber()`. The caller owns the subscriber's
 * lifecycle so test setups can substitute the in-memory transport without
 * monkey-patching anything.
 */
export function RealtimeProvider({ subscriber, children }: RealtimeProviderProps) {
    return (
        <RealtimeContext.Provider value={subscriber}>
            {children}
        </RealtimeContext.Provider>
    );
}

/**
 * Reads the RealtimeSubscriber from context. Throws if used outside a
 * RealtimeProvider — a missing provider is always a wiring bug, not a runtime
 * condition to handle.
 */
export function useRealtimeSubscriber(): RealtimeSubscriber {
    const subscriber = useContext(RealtimeContext);
    if (!subscriber) {
        throw new Error(
            'useRealtimeSubscriber must be used inside a <RealtimeProvider>'
        );
    }
    return subscriber;
}
